// Dynamic translation loader with code splitting
// Each language is loaded on demand to reduce initial bundle size

import type { Translation, Language } from './types';
export type { Translation, Language } from './types';
export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './types';

// Cache for loaded translations
const translationsCache: Partial<Record<Language, Translation>> = {};

// Loading state to prevent duplicate fetches
const loadingPromises: Partial<Record<Language, Promise<Translation>>> = {};

// Dynamic import functions for each language
const languageLoaders: Record<Language, () => Promise<{ default: Translation }>> = {
  nl: () => import('./locales/nl'),
  en: () => import('./locales/en'),
  fr: () => import('./locales/fr'),
  de: () => import('./locales/de'),
  es: () => import('./locales/es'),
  tr: () => import('./locales/tr'),
  bg: () => import('./locales/bg'),
  ro: () => import('./locales/ro'),
  sk: () => import('./locales/sk'),
};

/**
 * Load translations for a specific language
 * Returns cached version if already loaded
 */
export async function loadTranslations(language: Language): Promise<Translation> {
  // Return cached if available
  if (translationsCache[language]) {
    return translationsCache[language]!;
  }

  // Return existing promise if already loading
  if (loadingPromises[language]) {
    return loadingPromises[language]!;
  }

  // Start loading
  const loadPromise = languageLoaders[language]()
    .then((module) => {
      translationsCache[language] = module.default;
      delete loadingPromises[language];
      return module.default;
    })
    .catch((error) => {
      console.error(`Failed to load translations for ${language}:`, error);
      delete loadingPromises[language];
      // Fallback to Dutch if loading fails
      if (language !== 'nl') {
        return loadTranslations('nl');
      }
      throw error;
    });

  loadingPromises[language] = loadPromise;
  return loadPromise;
}

/**
 * Preload translations for better UX
 * Call this on app init or when user changes language preference
 */
export function preloadTranslations(language: Language): void {
  loadTranslations(language).catch(() => {
    // Silently fail, will retry when actually needed
  });
}

/**
 * Check if translations are already loaded for a language
 */
export function isTranslationLoaded(language: Language): boolean {
  return !!translationsCache[language];
}

/**
 * Get a cached translation (synchronous)
 * Returns undefined if not loaded yet
 */
export function getCachedTranslation(language: Language): Translation | undefined {
  return translationsCache[language];
}

/**
 * Get a specific translation key (synchronous)
 * Falls back to Dutch, then to the key itself if not found
 * Note: Translations must be loaded first using loadTranslations()
 */
export function getTranslation(language: Language, key: string): string {
  const translations = translationsCache[language];
  const fallbackTranslations = translationsCache['nl'];

  if (translations) {
    const value = (translations as Record<string, unknown>)[key];
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      return value[0];
    }
  }

  // Fallback to Dutch
  if (fallbackTranslations && language !== 'nl') {
    const value = (fallbackTranslations as Record<string, unknown>)[key];
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      return value[0];
    }
  }

  // Return key as last resort
  return key;
}

/**
 * Get translation with async loading if needed
 * Prefer this for guaranteed translation availability
 */
export async function getTranslationAsync(language: Language, key: string): Promise<string> {
  await loadTranslations(language);
  // Also ensure Dutch is loaded for fallback
  if (language !== 'nl') {
    await loadTranslations('nl');
  }
  return getTranslation(language, key);
}

/**
 * Create a translator function for a specific language
 * Useful for components that need many translations
 */
export function createTranslator(language: Language): (key: string) => string {
  return (key: string) => getTranslation(language, key);
}

/**
 * Get all translation keys (useful for type checking)
 */
export type TranslationKey = keyof Translation;

// For backwards compatibility, preload Dutch on module load
// This ensures the default language is always available
loadTranslations('nl').catch(console.error);
